import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errorText } from "@/lib/api";
import { UploadPanel } from "./UploadPanel";

export const ChatInput = ({ step, state, busy, onSend, onRefresh }) => {
  const [text, setText] = useState("");
  const [note, setNote] = useState("");
  const inputRef = useRef(null);
  const nav = useNavigate();
  useEffect(() => { setText(""); setNote(""); setAskOpen(false); inputRef.current?.focus(); }, [step.id]);

  const submitText = (e) => { e.preventDefault(); if (!text.trim() || busy) return; onSend({ text }); setText(""); };
  const pick = (v) => !busy && onSend({ value: v });

  const [warn, setWarn] = useState("");
  const [askOpen, setAskOpen] = useState(false);
  const [pendingUrl, setPendingUrl] = useState(null);
  const googleStart = async () => {
    try { const { data } = await api.get("/sources/google/start"); setWarn(data.warning); setPendingUrl(data.url); }
    catch (e) { setNote(errorText(e)); }
  };

  const options = step.options || [];
  const isSelect = step.input === "select";

  return (
    <form onSubmit={submitText} className="flex flex-col gap-3" aria-label={`Answer for step ${step.id}`}>
      {step.input === "google_connect" && (
        <div className="ts-card p-4 text-sm" data-testid="google-connect-panel">
          {state.google_connected ? <p style={{ color: "var(--verified)" }} className="font-semibold">Google mailbox connected (read-only).</p> : (
            <>
              <button type="button" className="ts-btn ts-btn-primary" onClick={googleStart} data-testid="google-connect-button">Authorise read-only access with Google</button>
              {pendingUrl && <div role="dialog" className="mt-3 rounded-xl p-3 text-xs" style={{ background: "var(--blue-soft)" }} data-testid="google-pre-notice"><p data-testid="google-verification-warning">{warn}</p><div className="flex gap-2 mt-2"><button type="button" className="ts-btn ts-btn-primary h-8 text-xs" onClick={() => { window.location.href = pendingUrl; }} data-testid="google-continue">Continue</button><button type="button" className="ts-btn ts-btn-ghost h-8 text-xs" onClick={() => { setPendingUrl(null); setWarn(""); }} data-testid="google-cancel">Cancel</button></div></div>}
              {note && <p role="status" className="mt-3 rounded px-3 py-2" style={{ background: "var(--amber-soft)", color: "var(--amber)" }} data-testid="google-pending-note">{note}</p>}
            </>
          )}
        </div>
      )}
      {step.input === "upload" && <UploadPanel documents={state.documents} onChange={onRefresh} />}
      {step.input === "review" && state.detected_count > 0 && (
        <button type="button" className="ts-btn ts-btn-primary self-start" onClick={() => nav("/app/review")} data-testid="go-review-button">Review {state.detected_count} detected item{state.detected_count !== 1 ? "s" : ""}</button>
      )}
      {isSelect ? (
        <div className="flex gap-2">
          <select className="ts-input flex-1" aria-label="Choose an option" data-testid="chat-select" defaultValue="" onChange={(e) => e.target.value && pick(e.target.value)} disabled={busy}>
            <option value="" disabled>Choose…</option>
            {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      ) : options.length > 0 && (
        <div className="flex flex-wrap gap-2.5" role="group" aria-label="Quick answers">
          {options.map((o) => (
            <button key={String(o.value)} type="button" disabled={busy} onClick={() => pick(o.value)} data-testid={`chat-option-${String(o.value).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="rounded-full border-[1.5px] px-5 py-3 text-[15px] font-bold transition-colors hover:border-[var(--blue)] hover:text-[var(--blue-deep)] hover:bg-[var(--blue-soft)] disabled:opacity-50"
              style={{ borderColor: o.style === "ghost" ? "var(--line)" : "var(--blue)", background: "#fff", color: o.style === "ghost" ? "var(--muted-text)" : "var(--ink)" }}>
              {o.label}
            </button>
          ))}
        </div>
      )}
      {step.input === "text" ? (
        <div className="flex gap-2">
          <label htmlFor="chat-text" className="sr-only">{step.placeholder || "Your answer"}</label>
          <input id="chat-text" ref={inputRef} className="ts-input flex-1" value={text} onChange={(e) => setText(e.target.value)} maxLength={500} disabled={busy} placeholder={step.placeholder || "Type your answer"} data-testid="chat-text-input" autoComplete="off" />
          <button className="ts-btn ts-btn-primary" disabled={busy || !text.trim()} data-testid="chat-send-button">{busy ? "…" : "Send"}</button>
        </div>
      ) : step.input !== "none" && (
        askOpen ? (
          <div className="flex gap-2">
            <input id="chat-text" ref={inputRef} className="ts-input flex-1" value={text} onChange={(e) => setText(e.target.value)} maxLength={500} disabled={busy} placeholder="Ask Trustee a question…" data-testid="chat-text-input" autoComplete="off" />
            <button className="ts-btn ts-btn-primary" disabled={busy || !text.trim()} data-testid="chat-send-button">{busy ? "…" : "Send"}</button>
          </div>
        ) : <button type="button" className="ts-link text-xs self-start" onClick={() => setAskOpen(true)} data-testid="ask-question-link">Ask a question</button>
      )}
    </form>
  );
};
